<script setup lang='ts'>
import { isClient, timeFormat, timeFrom, timeStamp } from '@qctsw/utils'
import { APP_ROUTER, addHost, imageTransform } from '@qctsw/common'
import { useComplaintDetail } from '../hooks/detail'
import RelativeCollective from '../components/RelativeCollective.vue'
import Affix from '../components/Affix.vue'
import Vendor from '../components/Vendor.vue'
import ComplaintMore from '../components/ComplaintMore.vue'
import ComplaintScore from '@/pages/user/components/ComplaintScore.vue'
import ContentLink from '@/pages/news/components/ContentLink.vue'
import { getDisposeTypeCode, getScoreText, stepIndex } from '@/shared/function'
import ComplaintDetailSupplement from '@/pages/user/components/ComplaintDetailSupplement.vue'
import UserComplaint from '@/pages/complaint/components/UserComplaint.vue'
import { AD_KEY, type ComplaintDetailResponse } from '~/server/types'
import ComplaintFeedBack from '@/pages/user/components/ComplaintFeedBack.vue'
import watermark from '@/composables/useWater'

const dataType = 'complaint'

const {
  id,
  detailData,
  state,

  tableData,
  manufacturerResult, // 厂商结果
  feedbackList,
  initHandler,
} = useComplaintDetail()

function genBrandList(item: ComplaintDetailResponse['collectiveLs'][number]) {
  const brandList: { brandId: number, brandName: string }[] = []
  for (const i of item.carSeriesLs) {
    if (brandList.findIndex(v => v.brandId === i.carBrandId) !== -1)
      continue

    brandList.push({
      brandId: i.carBrandId,
      brandName: i.carBrandName,
    })
  }
  return brandList
}

useActivated({
  activatedFn: () => {
    useCommonFavoriteOrCollectEvent().reset()
    useCommonFavoriteOrCollectEvent().on((data) => {
      if (!detailData.value)
        return
      if (data.type === 'favorite') {
        detailData.value.good = !detailData.value.good
        detailData.value.goodCount += detailData.value.good ? 1 : -1
      }
      else if (data.type === 'collect') {
        detailData.value.collect = !detailData.value.collect
      }
    })
  },
  deactivatedFn: () => {
    useCommonFavoriteOrCollectEvent().reset()
  },
})

const contentRef = ref<HTMLElement>()
if (isClient) {
  useActivatedWatch(() => [
    watch(contentRef, () => {
      if (contentRef.value)
        watermark.set('汽车投诉网', contentRef.value)
    }, { immediate: true }),
  ])
}

const { qrcode, getQrcode } = useMiniProgramQrcode()
if (isClient)
  getQrcode({ page: 'subpages/pages/complaint/detail', scene: `${id.value}`, envVersion: 'release' })

await initHandler()

// TODO 图片水印
</script>

<template>
  <div bg-slate-1>
    <div class="public-container public-page flex">
      <div class="flex-grow min-w-0 relative ">
        <!-- TODO: AD_KEY.E1 -->

        <BaseAd :ad-key="AD_KEY.E1" />
        <UtilLoadState :state="state" :reload-handler="() => initHandler()">
          <template v-if="detailData">
            <div ref="contentRef" class="p-sm rounded-md bg-white overflow-hidden relative ">
              <div v-if=" detailData.carComplainStatusCode === 4 && detailData.complainScoreCode !== null" class="score">
                <ComplaintScore :score="detailData.complainScoreCode" />
              </div>
              <div v-if="detailData.isSurveyBottom" class="tips">
                一追到底
              </div>
              <div class="px-30 text-title text-6.5 font-bold text-center">
                {{ detailData.title }}
              </div>
              <div class="flex items-center px-sm mt-xl text-gray-5 text-3.5">
                <NuxtLink
                  v-if="detailData.carVendorId && (detailData.vendorName || detailData.brandName)" class="mr-sm border-1 border-blue-5 text-blue-5 rounded-md px-2 py-0.5"
                  :to="APP_ROUTER.complaintSearch({ carBrandId: detailData.carBrandId, carBrandName: detailData.brandName })"
                >
                  {{ detailData.vendorName || detailData.brandName }}
                </NuxtLink>
                <NuxtLink
                  v-if="detailData.carBrandId && detailData.carSeriesId && detailData.seriesName" class="mr-sm border-1 border-blue-5 text-blue-5 rounded-md px-2 py-0.5"
                  :to="APP_ROUTER.complaintSearch({ carBrandId: detailData.carBrandId, carSeriesId: detailData.carSeriesId, carBrandName: detailData.brandName, carSeriesName: detailData.seriesName })"
                >
                  {{ detailData.seriesName }}
                </NuxtLink>
                <div class="mr-4">
                  {{ timeFrom(detailData.createTime) }}
                </div>
                <UtilContentTips
                  size="14"
                  :data-id="parseInt(id ?? '-1')"
                  :is-good="detailData.good"
                  :is-collect="detailData.collect"
                  :data-type="dataType"
                  can-set-good
                  :tips-data="{
                    lookCount: detailData.lookCount ?? 0,
                    favorCount: detailData.goodCount ?? 0,
                    collection: 0,
                  }"
                />
                <UtilMiniQrcode :qrcode="qrcode" />
              </div>

              <ContentLink
                v-if="detailData?.collectiveLs && detailData?.collectiveLs.length"
                title="返回主投诉"
                :link="APP_ROUTER.collectiveDetail('')"
                :data-list="detailData.collectiveLs"
              />
              <ContentLink
                v-if="detailData?.mediaLs && detailData?.mediaLs.length"
                title="媒体跟进"
                :link="APP_ROUTER.newsDetail('')"
                :data-list="detailData.mediaLs.map((item) => ({ id: undefined, link: item.articleLink, title: item.articleTitle }))"
              />
              <ContentLink
                v-if="detailData?.articleLs && detailData?.articleLs.length"
                title="关联文章"
                :link="APP_ROUTER.newsDetail('')"
                :data-list="detailData.articleLs.map((item) => ({ id: item.articleId, title: item.articleTitle }))"
              />

              <div class="mt-sm w-full rounded-1.5">
                <BasicDetailTable item-width="50%" :table-data="tableData" />
              </div>
              <div class="step flex items-center mt-6 rounded-1.5 shadow overflow-hidden border-1 border-slate-1">
                <div class="write-vertical-right w-15 min-h-25 bg-blue-5 text-white text-6 flex items-center justify-center">
                  进度
                </div>
                <div class="flex-grow min-w-0 overflow-hidden px-3 flex justify-center">
                  <BasicStepComplaint type="detail" :active="stepIndex(detailData.carComplainStatusCode)" :score="detailData.carComplainStatusCode === 4 ? detailData.complainScoreCode : undefined" />
                </div>
              </div>

              <div v-if="detailData.complainScoreCode == 2" class="p-sm rounded-md bg-white mt-sm">
                <NTimeline>
                  <NTimelineItem v-for="(item) in manufacturerResult" :key="item.id" type="info">
                    <BasicTimeLineCardMessage
                      :tip="timeFrom(item.createTime, 'YYYY-MM-DD HH:mm')"
                      :title="item.isDefault ? '网站上传结果' : getDisposeTypeCode(item.disposeTypeCode)"
                      :sub-title="item.isDefault ? item.defaultLabel : ''"
                      :content="item.isDefault ? item.defaultResult : item.companyResult || ''"
                      :supplement="item.isDefault ? [] : item.replyLs"
                    />
                  </NTimelineItem>
                  <NTimelineItem v-if="detailData.complainScoreCode != null && detailData.carComplainStatusCode === 4" type="success">
                    <BasicTimeLineCardMessage
                      tip="投诉人打分"
                      :title="`${detailData.complainScoreCode}分，${getScoreText(detailData.complainScoreCode)}`"
                      :content="detailData.scoreRemark ?? ''"
                    />
                  </NTimelineItem>
                </NTimeline>
              </div>
              <n-card v-if="detailData.baseInfo && detailData.baseInfo?.aiSuggest && !(detailData.carComplainStatusCode === 0 || (detailData.undo))" class="rounded mt-6" style="    background-image: linear-gradient(180deg, rgba(247, 232, 255, .54), rgba(191, 223, 255, .35));">
                <n-collapse default-expanded-names="1" arrow-placement="right">
                  <n-collapse-item title="989" name="1">
                    <template #header>
                      <div class=" flex items-center ">
                        <i class="i-custom-jy text-6 color-primary" />
                        <span class="font-500 color-primary text-4"> 投诉建议</span>
                      </div>
                    </template>
                    <p class="text-4" v-html="detailData.baseInfo.aiSuggest" />
                    <p class="text-3 mt-3 color-primary">
                      内容由DeepSeek-R1模型生成
                    </p>
                  </n-collapse-item>
                </n-collapse>
              </n-card>

              <!-- 撤帖/审核中 -->
              <div v-if="detailData.carComplainStatusCode === 0 || (detailData.undo)" class="undo-container relative">
                <!-- 撤帖成功 -->
                <img style="width: 100%;height: 100%;" src="/images/content.jpg">
                <div v-if="detailData.carComplainStatusCode === 0" class="audit-tips text-center">
                  <div class="undo-text">
                    内容审核中
                  </div>
                </div>
                <div v-else class="undo-tips text-center">
                  <div class="undo-text">
                    车主撤诉申请:<span>{{ timeFormat(detailData.userUndoTime, "YYYY-MM-DD HH:mm") }}</span>
                  </div>
                  <div class="undo-text">
                    撤诉成功时间:<span>{{
                      detailData.undoFinishTime
                        ? timeFormat(detailData.undoFinishTime, "YYYY-MM-DD HH:mm")
                        : timeFormat(timeStamp(detailData.userUndoTime as string, 10), "YYYY-MM-DD HH:mm")
                    }}</span>
                  </div>
                </div>
              </div>
              <template v-else>
                <div v-if="detailData.refComplainId && detailData.refComplainVo" class="my-6 p-3 rounded-md border-1 border-slate-2">
                  <div class="text-4 font-bold mb-3">
                    <span>上次投诉标题: </span>
                    <span>{{ detailData.refComplainVo.title }}</span>
                  </div>
                  <div class="content" v-html="detailData.refComplainVo.complainContent" />
                  <div>
                    <div class="text-4 font-bold mb-1">
                      上次投诉最终结果
                    </div>
                    <div>
                      {{ detailData.refComplainVo.disposeContent }}
                    </div>
                  </div>
                </div>
                <article>
                  <div class="relative z-1">
                    <div class="content my-6 px-sm">
                      <div v-html="detailData.baseInfo.content" />
                    </div>
                    <!-- 补充 -->
                    <div v-if="detailData.datumLs && detailData.datumLs.length" class="mt-8.5">
                      <ComplaintDetailSupplement :is-audit-status="false" :is-bold="true" :data="detailData.datumLs" />
                    </div>
                    <div v-if="detailData.webReplenishContent && detailData.webReplenishTime" class="mt-8.5">
                      <ComplaintDetailSupplement
                        :is-bold="true" :is-audit-status="false" title="网站补充内容"
                        :data="[{
                          auditStatusCode: 1,
                          auditExplain: '',
                          auditTime: '',
                          createTime: detailData.webReplenishTime,
                          datumContent: `${detailData.webReplenishTitle}${detailData.webReplenishContent}`,
                        }]"
                      />
                    </div>
                    <!-- 撤帖申请成功 -->
                    <div v-if="detailData.userUndoTime">
                      <div class="undo-tipsing flex justify-center">
                        <div>
                          <div class="undo-text">
                            车主撤诉申请时间:<span>{{ timeFormat(detailData.userUndoTime, "YYYY-MM-DD HH:mm") }}</span>
                          </div>
                          <div class="undo-text">
                            预计撤诉成功时间:<span>{{
                              detailData.undoFinishTime
                                ? timeFormat(detailData.undoFinishTime, "YYYY-MM-DD")
                                : timeFormat(timeStamp(detailData.userUndoTime, 10), "YYYY-MM-DDm")
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 图片 -->
                  <template v-if="detailData.imgLs?.length">
                    <n-image-group>
                      <n-space>
                        <NCarousel :autoplay="false" :loop="false" :show-arrow="detailData.imgLs?.length >= 2" class="mt-6 rounded-md overflow-hidden h-100">
                          <NImage
                            v-for="(item, index) in detailData.imgLs"
                            :key="index" :alt="detailData.title" class="w-full h-full"
                            object-fit="cover"
                            :src="imageTransform(item.imgUrl, { width: 1100, height: 500 })"
                            :preview-src="imageTransform(item.imgUrl)"
                          />
                        </NCarousel>
                      </n-space>
                    </n-image-group>
                  </template>
                  <!-- 视频 -->
                  <template v-if="detailData.videoLs?.length">
                    <div
                      v-for="(item, index) in detailData.videoLs" :key="index"
                      class="reset video mt-6 rounded-1.5 overflow-hidden bg-black"
                    >
                      <video
                        class="w-full h-full"
                        controls
                        controlslist="nodownload"
                        :poster="addHost(item.complainImgUrl) ?? ''"
                        :src="addHost(item.videoUrl) ?? ''"
                      />
                    </div>
                  </template>
                  <!-- 音频 -->
                  <template v-if="detailData.audioLs?.length">
                    <div
                      v-for="(v, i) in detailData.audioLs" :key="i"
                      class="w-75 h-12.5 mr-4 mt-4 rounded-1.5 flex items-center overflow-hidden"
                    >
                      <audio controls width="100%" class="w-full h-full" :src="addHost(v.audioUrl) ?? ''" />
                    </div>
                  </template>
                </article>
                <ContentLink
                  v-if="detailData?.collectiveLs && detailData?.collectiveLs.length"
                  title="返回主投诉"
                  :link="APP_ROUTER.collectiveDetail('')"
                  :data-list="detailData.collectiveLs"
                />
                <ContentLink
                  v-if="detailData?.mediaLs && detailData?.mediaLs.length"
                  title="媒体跟进"
                  :link="APP_ROUTER.newsDetail('')"
                  :data-list="detailData.mediaLs.map((item) => ({ id: undefined, link: item.articleLink, title: item.articleTitle }))"
                />
                <ContentLink
                  v-if="detailData?.articleLs && detailData?.articleLs.length"
                  title="关联文章"
                  :link="APP_ROUTER.newsDetail('')"
                  :data-list="detailData.articleLs.map((item) => ({ id: item.articleId, title: item.articleTitle }))"
                />
              </template>
              <div class="p-sm rounded-md bg-white mt-sm">
                <NTimeline>
                  <NTimelineItem v-for="(item) in manufacturerResult" :key="item.id" type="info">
                    <BasicTimeLineCardMessage
                      :tip="timeFrom(item.createTime, 'YYYY-MM-DD HH:mm')"
                      :title="item.isDefault ? '网站上传结果' : getDisposeTypeCode(item.disposeTypeCode)"
                      :sub-title="item.isDefault ? item.defaultLabel : ''"
                      :content="item.isDefault ? item.defaultResult : item.companyResult || ''"
                      :supplement="item.isDefault ? [] : item.replyLs"
                    />
                  </NTimelineItem>
                  <NTimelineItem v-if="detailData.complainScoreCode != null && detailData.carComplainStatusCode === 4" type="success">
                    <BasicTimeLineCardMessage
                      tip="投诉人打分"
                      :title="`${detailData.complainScoreCode}分，${getScoreText(detailData.complainScoreCode)}`"
                      :content="detailData.scoreRemark ?? ''"
                    />
                  </NTimelineItem>
                </NTimeline>
              </div>
              <ClientOnly>
                <UtilCommentOperation
                  class="mt-xl"
                  :is-good="detailData.good"
                  :data-id="Number(id)"
                  :data-type="dataType"
                  :is-collect="detailData.collect"
                />
                <Affix
                  :portal-user-id="detailData.portalUserId"
                  :car-brand-id="detailData.carBrandId"
                  :car-series-id="!isNaN(Number(detailData.carSeriesId)) ? Number(detailData.carSeriesId) : 0"
                  :is-survey-bottom="detailData.isSurveyBottom"
                />
              </ClientOnly>
            </div>
            <UserComplaint class="p-sm rounded-md bg-white mt-sm" :complaint-id="Number(id)" :portal-user-id="detailData?.portalUserId" />
            <!-- 结果 -->
            <div class="p-sm rounded-md bg-white mt-sm">
              <!-- 交流反馈 -->
              <div v-if="feedbackList && feedbackList.length" class="mt-xl">
                <h4 class="font-bold">
                  交流反馈
                </h4>
                <ComplaintFeedBack :list="feedbackList" />
              </div>
              <!-- 多次投诉列表 -->
              <div v-if="detailData.moreLs && detailData.moreLs.length" class="rounded-md overflow-hidden border-1 border-slate-2 mt-xl">
                <ComplaintMore
                  :more-ls="detailData.moreLs"
                  :vendor-name="detailData.vendorName || ''"
                  :brand-name="detailData.brandName || ''"
                  :series-name="detailData.seriesName || ''"
                  :model-name="detailData.modelName || ''"
                />
              </div>
              <!-- 集体投诉信息 -->
              <template v-if="detailData.collectiveLs">
                <RelativeCollective
                  v-for="v, i in detailData.collectiveLs" :key="i"
                  class="mb-sm"
                  :brand-list="genBrandList(v)"
                  :series-list="(v.carSeriesLs ?? []).map((item) => ({ brandId: item.carBrandId, brandName: item.carBrandName, seriesId: item.carSeriesId, seriesName: item.carSeriesName }))"
                  :complain-explain="v.complainExplain"
                  :measure="v.measure"
                >
                  <template #title>
                    <div class="mr-4 flex items-center">
                      涉及【<span class="truncate flex-1" :title="v.title">{{ v.title }}</span>】的集体
                    </div>
                    <div class="flex-shrink-0 font-normal cursor-pointer flex-grow min-w-0 text-right hover:text-blue-5">
                      <NuxtLink :to="APP_ROUTER.collectiveDetail(v.id)">
                        <span>进入集体投诉({{ v.complainCount }}宗)</span>
                        <i class="i-material-symbols:keyboard-double-arrow-right-rounded" />
                      </NuxtLink>
                    </div>
                  </template>
                </RelativeCollective>
              </template>
              <!-- 数据统计 -->
              <Vendor
                class="mt-sm bg-slate-1 p-4 rounded-1.5"
                :vendor-name="detailData.vendorName || ''"
                :vendor-credit="detailData?.vendorCredit"
              />
              <div class=" bg-slate-1 text-3.25 p-xs rounded text-slate-5 mt-sm indent-2em">
                免责声明：站内会员言论仅代表个人观点，并不代表汽车投诉网[QcTsw.Com]同意其说法或描述，不承担相关法律责任。未经投诉人及汽车投诉网共同授权许可，任何单位与个人不得转载，如需转载请联系!
              </div>
            </div>
            <ClientOnly>
              <div v-if="detailData" class="bg-white rounded-md mt-lg p-sm">
                <LayoutComment :data-id="Number(id)" :data-type="dataType" />
              </div>
            </ClientOnly>
          </template>
          <BasicEmpty v-else text="未找到相关投诉" />
        </UtilLoadState>
      </div>
      <div class="flex-shrink-0 w-[calc(100%-53rem)] pl-xl">
        <ClientOnly>
          <BaseAd :ad-key="AD_KEY.E2" />
          <RankRelativeComplaint v-if="detailData" class=" mb-xl" :series-id="detailData.carSeriesId" :embedded="false" />
          <BaseAd :ad-key="AD_KEY.E3" />

          <RankHotComplaint class="mb-xl " :embedded="false" />

          <RankHotCollective class=" sticky top-25" :embedded="false" />
        </ClientOnly>
      </div>
      <UtilShareFixed />
    </div>
  </div>
</template>

<style lang='scss' scoped>
.n-carousel {

&:hover{
  &.n-carousel--bottom {

    :deep(.n-carousel__arrow-group) {
    display: block;
  }
  }

}
&.n-carousel--bottom :deep(.n-carousel__arrow-group) {
  position: static;
  display: none;

  .n-carousel__arrow {

    --uno: bg-black bg-opacity-40 h-100px text-10 w-10  ;
    position: absolute;
    right: 0;
    top:50%;
    transform: translateY(-50%);
    &:last-child{
      border-radius: 10px 0 0px 10px;
    }

    &:first-child {
      border-radius: 0px 10px 10px 0px;
      left: 0;
    }

  }
}

}

.n-carousel.n-carousel--bottom :deep(.n-carousel__arrow-group) {
--uno: bottom-15;

}
@import url('@/assets/styles/status.scss');
@import url('./style.scss');
</style>
